import React, { useEffect } from 'react';
import { Button, Paper, Stacker } from '@honeycomb/ui-core';
import { HotelAvailabilityErrors, parseBoolean, TripType, useFlightSearchResults, useGlobalContext, } from '@honeycomb/data';
import { reduceMockErrorAttemptsRouting } from '@honeycomb/routing';
import { BetaEstabAction, trackBetaEstabAction } from '@honeycomb/tracking';
import { useBackgroundFlight } from '../../background-flight-search/useBackgroundFlight';
import { skipHotelFlightSearchResults, validSelectedFlight } from '../../background-flight-search/utils';
import { HotelBasketActionType, useHotelBasketContext } from '../HotelBasketProvider';
import { HotelFlightsSummaryCard } from './HotelFlightsSummaryCard/HotelFlightsSummaryCard';
import { HotelFlightsUnavailable } from './HotelFlightsUnavailable/HotelFlightsUnavailable';
import { FlightRetry } from '../FlightRetryNotification/FlightRetry';
import { useHotelDetailsContext } from '../HotelDetails/HotelDetails.context';
import { HotelChangeFlightError } from './HotelChangeFlightError';
import { HotelFlightsSummaryHeading } from './HotelFlightsSummaryHeading';
export function HotelFlightsSummary({ hotelDetails, requiresBackgroundFlightSearch, onClickChangeFlight, }) {
    var _a, _b, _c;
    const { dispatch, hotelBasket: { selectedFlight, waitingForFlights, tripStateId: flightTripStateId, availabilityErrors, hotelCheckInDate, estabId, }, } = useHotelBasketContext();
    const { configSettings: { EnableMockErrors, DepartureFlightCutOff }, resourceStrings: { HC_CHANGE_FLIGHT }, } = useGlobalContext();
    const { state: { onScrollToSection }, } = useHotelDetailsContext();
    const hasBgFlightError = availabilityErrors === null || availabilityErrors === void 0 ? void 0 : availabilityErrors.includes(HotelAvailabilityErrors.FLIGHT_ERROR);
    const hasNoFlightIdError = availabilityErrors === null || availabilityErrors === void 0 ? void 0 : availabilityErrors.includes(HotelAvailabilityErrors.FLIGHT_ERROR_NO_FLIGHT_ID);
    const hasChangeFlightError = availabilityErrors === null || availabilityErrors === void 0 ? void 0 : availabilityErrors.includes(HotelAvailabilityErrors.CHANGE_FLIGHT_ERROR);
    const sideEffect = (data) => {
        var _a, _b, _c, _d, _e, _f, _g;
        const flightsRequest = (_b = (_a = data === null || data === void 0 ? void 0 : data.backgroundFlightSearch) === null || _a === void 0 ? void 0 : _a.tripState) === null || _b === void 0 ? void 0 : _b.flightsRequest;
        const flight = (_c = data === null || data === void 0 ? void 0 : data.backgroundFlightSearch) === null || _c === void 0 ? void 0 : _c.flight;
        if (!((_e = (_d = data === null || data === void 0 ? void 0 : data.backgroundFlightSearch) === null || _d === void 0 ? void 0 : _d.tripState) === null || _e === void 0 ? void 0 : _e.tripStateId)) {
            return;
        }
        // need to update tripstate as well
        dispatch({
            type: HotelBasketActionType.SET_SELECTED_FLIGHT,
            payload: {
                flightDetailsId: flightsRequest === null || flightsRequest === void 0 ? void 0 : flightsRequest.flightDetailsId,
                flightId: flightsRequest === null || flightsRequest === void 0 ? void 0 : flightsRequest.flightId,
                inboundLeg: flight === null || flight === void 0 ? void 0 : flight.inboundLeg,
                outboundLeg: flight === null || flight === void 0 ? void 0 : flight.outboundLeg,
            },
        });
        dispatch({
            type: HotelBasketActionType.SET_TRIP_STATE_ID,
            payload: (_g = (_f = data === null || data === void 0 ? void 0 : data.backgroundFlightSearch) === null || _f === void 0 ? void 0 : _f.tripState) === null || _g === void 0 ? void 0 : _g.tripStateId,
        });
        if (hasNoFlightIdError) {
            dispatch({
                type: HotelBasketActionType.REMOVE_AVAILABILITY_ERRORS,
                payload: HotelAvailabilityErrors.FLIGHT_ERROR_NO_FLIGHT_ID,
            });
        }
    };
    const errorSideEffect = () => {
        trackBetaEstabAction(BetaEstabAction.AVAILABILITY_ERROR_NO_FLIGHT, TripType.Holiday);
        dispatch({
            type: HotelBasketActionType.SET_AVAILABILITY_ERRORS,
            payload: HotelAvailabilityErrors.FLIGHT_ERROR,
        });
        onScrollToSection === null || onScrollToSection === void 0 ? void 0 : onScrollToSection('flights');
        if (parseBoolean(EnableMockErrors)) {
            reduceMockErrorAttemptsRouting();
        }
    };
    const hasSelectedFlight = validSelectedFlight(selectedFlight);
    // Can destructure the trigger to call background flight search
    // Could be useful in error / no data situatons
    const { triggerBackgroundFlightSearch } = useBackgroundFlight(hotelDetails, requiresBackgroundFlightSearch, hasSelectedFlight, hotelCheckInDate, parseInt(DepartureFlightCutOff), sideEffect, errorSideEffect);
    const [flightSearchResults] = useFlightSearchResults([], hotelCheckInDate, '', skipHotelFlightSearchResults(selectedFlight, flightTripStateId, hotelCheckInDate), flightTripStateId, parseInt(estabId));
    const flightsUnavailable = !waitingForFlights && !hasSelectedFlight;
    const totalFlights = (_c = (_b = (_a = flightSearchResults.data) === null || _a === void 0 ? void 0 : _a.flightSearchResults) === null || _b === void 0 ? void 0 : _b.totalFlights) !== null && _c !== void 0 ? _c : 0;
    const waitingForFlightAvailability = flightSearchResults.loading || !flightSearchResults.called;
    const retryBackgroundFlightSearch = () => {
        if (triggerBackgroundFlightSearch) {
            if (hasBgFlightError) {
                dispatch({
                    type: HotelBasketActionType.REMOVE_AVAILABILITY_ERRORS,
                    payload: HotelAvailabilityErrors.FLIGHT_ERROR,
                });
            }
            dispatch({
                type: HotelBasketActionType.SET_WAITING_FOR_FLIGHT,
                payload: true,
            });
            triggerBackgroundFlightSearch();
        }
    };
    useEffect(() => {
        if (hasNoFlightIdError) {
            retryBackgroundFlightSearch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasNoFlightIdError]);
    // Background flight error
    if (hasBgFlightError) {
        return (React.createElement(Paper, { py: 5, defaultGutters: true },
            React.createElement(Stacker, { spacing: 6 },
                React.createElement(HotelFlightsSummaryHeading, null),
                React.createElement(FlightRetry, { onTriggerHolidayEstabFlightSearch: retryBackgroundFlightSearch }))));
    }
    // Flights unavailable error
    if (flightsUnavailable) {
        return (React.createElement(Paper, { py: 5, defaultGutters: true },
            React.createElement(Stacker, { spacing: 6 },
                React.createElement(HotelFlightsSummaryHeading, null),
                React.createElement(HotelFlightsUnavailable, null))));
    }
    // Change flight  error
    if (hasChangeFlightError) {
        return (React.createElement(Paper, { py: 5, defaultGutters: true },
            React.createElement(Stacker, { spacing: 6 },
                React.createElement(HotelFlightsSummaryHeading, null),
                React.createElement(HotelChangeFlightError, null))));
    }
    return (React.createElement(Paper, { py: 5, defaultGutters: true },
        React.createElement(Stacker, { spacing: 6 },
            React.createElement(HotelFlightsSummaryHeading, { waitingForFlightAvailability: waitingForFlightAvailability, totalFlights: totalFlights }),
            (waitingForFlights || hasSelectedFlight) && (React.createElement(React.Fragment, null,
                React.createElement(HotelFlightsSummaryCard, { requiresBackgroundFlightSearch: requiresBackgroundFlightSearch }),
                React.createElement(Button, { variant: "outlined", onClick: onClickChangeFlight, disabled: !hasSelectedFlight, "data-id": "change-flight-button" }, HC_CHANGE_FLIGHT))))));
}
